import {v4 as uuidv4} from 'uuid';
import {assertEvent, assign, setup} from 'xstate';

import {
  applyAddLayer,
  applyInitLayers,
  applyRemoveLayer,
  applySetActiveLayer,
  applyUpdateLayerName,
  buildInitialLayerContext,
  LayerBase,
  LayerContextBase,
  LayerMachineInputBase,
} from 'samples/plate-maps/layer-machine/sharedLayerActions';
import {
  ParameterFieldType,
  ParameterLayerInfo,
  PLATE_MAP_MAX_DATA_PARAMETER_LAYERS,
} from 'samples/plate-maps/PlateMapConstants';

const MACHINE_NAME = 'ParameterLayerMachine';

export type ParameterLayerData = ParameterLayerInfo & LayerBase;

export type ParameterLayerEvents =
  | {type: 'initLayers'; layers: Array<ParameterLayerData>}
  | {
      type: 'addLayer';
      layerId: string;
      name: string;
      fieldType: ParameterFieldType;
      unitSymbol?: string | null;
    }
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string};

export type ParameterLayerContext = LayerContextBase<ParameterLayerData>;

export type ParameterLayerMachineInput = LayerMachineInputBase<ParameterLayerData>;

export function createParameterLayerId(): string {
  return `parameter-layer-${uuidv4()}`;
}

export const ParameterLayerMachine = setup({
  types: {
    input: {} as ParameterLayerMachineInput,
    context: {} as ParameterLayerContext,
    events: {} as ParameterLayerEvents,
  },
  actions: {
    initLayers: assign(({context, event}) => {
      assertEvent(event, 'initLayers');
      return applyInitLayers(context, event.layers, PLATE_MAP_MAX_DATA_PARAMETER_LAYERS, MACHINE_NAME);
    }),

    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      const newLayer: ParameterLayerData = {
        layerId: event.layerId,
        name: event.name,
        fieldType: event.fieldType,
        unitSymbol: event.unitSymbol,
      };
      return applyAddLayer(context, newLayer, PLATE_MAP_MAX_DATA_PARAMETER_LAYERS, MACHINE_NAME);
    }),

    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');
      return applyRemoveLayer(context, event.layerId);
    }),

    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');
      return applySetActiveLayer(context, event.layerId);
    }),

    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');
      return applyUpdateLayerName(context, event.layerId, event.name);
    }),
  },
}).createMachine({
  context: ({input}) => buildInitialLayerContext(input, PLATE_MAP_MAX_DATA_PARAMETER_LAYERS, MACHINE_NAME),

  id: 'ParameterLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        initLayers: {actions: 'initLayers'},
        addLayer: {actions: 'addLayer'},
        removeLayer: {actions: 'removeLayer'},
        setActiveLayer: {actions: 'setActiveLayer'},
        updateLayerName: {actions: 'updateLayerName'},
      },
    },
  },
});
